import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  FileText,
  Image as ImageIcon,
  Layout,
  MessageSquare,
  Globe,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  Users,
  TrendingUp,
  Settings,
  Upload,
  Copy,
  Power,
  PowerOff,
  RefreshCw,
  Loader,
  ExternalLink,
  Timer,
  MousePointer,
  ChevronDown,
  EyeOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { formatDate } from "../lib/utils";
import {
  useBanners,
  useFAQs,
  useTestimonials,
  useLaunchSplashImages,
} from "../hooks/useContent";
import {
  AdminBanner,
  AdminFAQ,
  AdminTestimonial,
} from "../services/contentService";
import {
  LaunchSplashImage,
  LaunchSplashStatus,
} from "../services/launchSplashService";
import { CreateBannerModal } from "../components/modals/CreateBannerModal";
import { CreateFAQModal } from "../components/modals/CreateFAQModal";
import { CreateTestimonialModal } from "../components/modals/CreateTestimonialModal";
import { EditBannerModal } from "../components/modals/EditBannerModal";
import { EditFAQModal } from "../components/modals/EditFAQModal";
import { EditTestimonialModal } from "../components/modals/EditTestimonialModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { useAuth } from "../context/AuthContext";

const contentTypes = ["All", "banners", "launchSplash", "faqs", "testimonials"];
const contentStatuses = [
  "All",
  "active",
  "inactive",
  "scheduled",
  "draft",
  "published",
  "pending",
  "featured",
];

export function Content() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState("banners");
  const [isCreateBannerModalOpen, setIsCreateBannerModalOpen] = useState(false);
  const [isCreateFAQModalOpen, setIsCreateFAQModalOpen] = useState(false);
  const [isCreateTestimonialModalOpen, setIsCreateTestimonialModalOpen] =
    useState(false);
  const [isEditBannerModalOpen, setIsEditBannerModalOpen] = useState(false);
  const [isEditFAQModalOpen, setIsEditFAQModalOpen] = useState(false);
  const [isEditTestimonialModalOpen, setIsEditTestimonialModalOpen] =
    useState(false);
  const [selectedBanner, setSelectedBanner] = useState<AdminBanner | null>(
    null
  );
  const [selectedFAQ, setSelectedFAQ] = useState<AdminFAQ | null>(null);
  const [selectedTestimonial, setSelectedTestimonial] =
    useState<AdminTestimonial | null>(null);

  // FAQ confirmation modals
  const [showDeleteFAQModal, setShowDeleteFAQModal] = useState(false);
  const [faqActionLoading, setFAQActionLoading] = useState(false);
  const [pendingFAQAction, setPendingFAQAction] = useState<{
    faq: AdminFAQ;
    action: "delete";
  } | null>(null);

  // Testimonial confirmation modals
  const [showUnpublishTestimonialModal, setShowUnpublishTestimonialModal] =
    useState(false);
  const [showDeleteTestimonialModal, setShowDeleteTestimonialModal] =
    useState(false);
  const [testimonialActionLoading, setTestimonialActionLoading] =
    useState(false);
  const [pendingTestimonialAction, setPendingTestimonialAction] = useState<{
    testimonial: AdminTestimonial;
    action: "unpublish" | "publish" | "delete";
  } | null>(null);
  const [launchSplashFilter, setLaunchSplashFilter] = useState<
    LaunchSplashStatus | "all"
  >("active");
  const [launchSplashFile, setLaunchSplashFile] = useState<File | null>(null);
  const [launchSplashPreview, setLaunchSplashPreview] = useState<string | null>(
    null
  );
  const [launchSplashAltText, setLaunchSplashAltText] = useState("");
  const [launchSplashRoute, setLaunchSplashRoute] = useState("");
  const [launchSplashUploadLoading, setLaunchSplashUploadLoading] =
    useState(false);
  const [launchSplashActionId, setLaunchSplashActionId] = useState<
    string | null
  >(null);
  const [pendingLaunchSplashDelete, setPendingLaunchSplashDelete] =
    useState<LaunchSplashImage | null>(null);
  const launchSplashInputRef = useRef<HTMLInputElement | null>(null);
  const { currentUser } = useAuth();

  // Use hooks for real Firebase data
  const {
    banners,
    loading: bannersLoading,
    error: bannersError,
    refresh: refreshBanners,
    updateBanner,
    addBanner,
  } = useBanners();
  const {
    faqs,
    loading: faqsLoading,
    error: faqsError,
    refresh: refreshFAQs,
    addFAQ,
    updateFAQ,
    deleteFAQ,
  } = useFAQs();
  const {
    testimonials,
    loading: testimonialsLoading,
    error: testimonialsError,
    refresh: refreshTestimonials,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
  } = useTestimonials();
  const {
    launchSplashImages,
    loading: launchSplashLoading,
    error: launchSplashError,
    refresh: refreshLaunchSplash,
    createLaunchSplash,
    updateLaunchSplashStatus,
    deleteLaunchSplash,
  } = useLaunchSplashImages();

  // Calculate statistics
  const activeBannerCount = useMemo(() => {
    return banners.filter((b) => b.isActive).length;
  }, [banners]);

  const publishedFAQsCount = useMemo(() => {
    return faqs.filter((f) => f.isPublished).length;
  }, [faqs]);

  const faqsPublishedPercentage = useMemo(() => {
    if (faqs.length === 0) return "0.0";
    return ((publishedFAQsCount / faqs.length) * 100).toFixed(1);
  }, [faqs, publishedFAQsCount]);

  const publishedTestimonialsCount = useMemo(() => {
    return testimonials.filter((t) => t.status === "published").length;
  }, [testimonials]);

  const testimonialAverageRating = useMemo(() => {
    const publishedTestimonials = testimonials.filter(
      (t) => t.status === "published"
    );
    if (publishedTestimonials.length === 0) return "0.0";

    const totalRating = publishedTestimonials.reduce(
      (sum, testimonial) => sum + testimonial.rating,
      0
    );
    return (totalRating / publishedTestimonials.length).toFixed(1);
  }, [testimonials]);

  const activeLaunchSplashCount = useMemo(() => {
    return launchSplashImages.filter((image) => image.status === "active")
      .length;
  }, [launchSplashImages]);

  const filteredLaunchSplashImages = useMemo(() => {
    if (launchSplashFilter === "all") return launchSplashImages;
    return launchSplashImages.filter(
      (image) => image.status === launchSplashFilter
    );
  }, [launchSplashImages, launchSplashFilter]);

  const totalActiveContentCount = useMemo(() => {
    return (
      activeBannerCount +
      publishedFAQsCount +
      publishedTestimonialsCount +
      activeLaunchSplashCount
    );
  }, [
    activeBannerCount,
    publishedFAQsCount,
    publishedTestimonialsCount,
    activeLaunchSplashCount,
  ]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "active":
      case "published":
        return {
          color: "bg-green-100 text-green-800",
          label: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "inactive":
      case "unpublished":
        return {
          color: "bg-gray-100 text-gray-800",
          label: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "scheduled":
        return { color: "bg-blue-100 text-blue-800", label: "Scheduled" };
      case "draft":
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          label: status.charAt(0).toUpperCase() + status.slice(1),
        };
      case "featured":
        return {
          color: "bg-jamaica-green/20 text-jamaica-green",
          label: "Featured",
        };
      default:
        return { color: "bg-gray-100 text-gray-800", label: "Unknown" };
    }
  };

  // Get background color for banners
  const getBannerBackgroundConfig = (
    backgroundColor: "jamaica-green" | "jamaica-black"
  ) => {
    switch (backgroundColor) {
      case "jamaica-green":
        return {
          color: "bg-jamaica-green/20 text-jamaica-green",
          label: "Green Theme",
        };
      case "jamaica-black":
        return {
          color: "bg-jamaica-black/20 text-jamaica-black",
          label: "Black Theme",
        };
      default:
        return { color: "bg-gray-100 text-gray-800", label: "Default" };
    }
  };

  // Handle banner status toggle
  const handleToggleBannerStatus = async (banner: AdminBanner) => {
    try {
      await updateBanner(banner.id, { isActive: !banner.isActive });
      toast.success(
        `Banner ${banner.isActive ? "deactivated" : "activated"} successfully`
      );
    } catch (error) {
      console.error("Error toggling banner status:", error);
      toast.error("Failed to update banner status");
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await Promise.all([
        refreshBanners(),
        refreshFAQs(),
        refreshTestimonials(),
        refreshLaunchSplash(),
      ]);
      toast.success("Content refreshed successfully");
    } catch (error) {
      console.error("Error refreshing content:", error);
      toast.error("Failed to refresh content");
    }
  };

  // Handle create content based on active tab
  const handleCreateContent = () => {
    switch (selectedTab) {
      case "banners":
        setIsCreateBannerModalOpen(true);
        break;
      case "faqs":
        setIsCreateFAQModalOpen(true);
        break;
      case "testimonials":
        setIsCreateTestimonialModalOpen(true);
        break;
      default:
        setIsCreateBannerModalOpen(true);
    }
  };

  // Handle edit banner
  const handleEditBanner = (banner: AdminBanner) => {
    setSelectedBanner(banner);
    setIsEditBannerModalOpen(true);
  };

  // Handle edit testimonial
  const handleEditTestimonial = (testimonial: AdminTestimonial) => {
    setSelectedTestimonial(testimonial);
    setIsEditTestimonialModalOpen(true);
  };

  // Handle edit FAQ
  const handleEditFAQ = (faq: AdminFAQ) => {
    setSelectedFAQ(faq);
    setIsEditFAQModalOpen(true);
  };

  // Handle FAQ delete action
  const handleFAQDeleteAction = (faq: AdminFAQ) => {
    setPendingFAQAction({ faq, action: "delete" });
    setShowDeleteFAQModal(true);
  };

  // Confirm FAQ deletion
  const handleConfirmFAQDelete = async () => {
    if (!pendingFAQAction) return;

    const { faq } = pendingFAQAction;
    setFAQActionLoading(true);

    try {
      await deleteFAQ(faq.id);
      toast.success("FAQ deleted successfully");
      setShowDeleteFAQModal(false);
      setPendingFAQAction(null);
    } catch (error) {
      console.error("Error deleting FAQ:", error);
      toast.error("Failed to delete FAQ");
    } finally {
      setFAQActionLoading(false);
    }
  };

  // Handle testimonial publish/unpublish action
  const handleTestimonialStatusAction = (
    testimonial: AdminTestimonial,
    action: "unpublish" | "publish"
  ) => {
    setPendingTestimonialAction({ testimonial, action });
    setShowUnpublishTestimonialModal(true);
  };

  // Handle testimonial delete action
  const handleTestimonialDeleteAction = (testimonial: AdminTestimonial) => {
    setPendingTestimonialAction({ testimonial, action: "delete" });
    setShowDeleteTestimonialModal(true);
  };

  // Confirm testimonial status change
  const handleConfirmTestimonialStatusChange = async () => {
    if (!pendingTestimonialAction) return;

    const { testimonial, action } = pendingTestimonialAction;
    setTestimonialActionLoading(true);

    try {
      const newStatus = action === "publish" ? "published" : "unpublished";
      await updateTestimonial(testimonial.id, { status: newStatus });
      toast.success(`Testimonial ${action}ed successfully`);
      setShowUnpublishTestimonialModal(false);
      setPendingTestimonialAction(null);
    } catch (error) {
      console.error("Error updating testimonial status:", error);
      toast.error(`Failed to ${action} testimonial`);
    } finally {
      setTestimonialActionLoading(false);
    }
  };

  // Confirm testimonial deletion
  const handleConfirmTestimonialDelete = async () => {
    if (!pendingTestimonialAction) return;

    const { testimonial } = pendingTestimonialAction;
    setTestimonialActionLoading(true);

    try {
      await deleteTestimonial(testimonial.id);
      toast.success("Testimonial deleted successfully");
      setShowDeleteTestimonialModal(false);
      setPendingTestimonialAction(null);
    } catch (error) {
      console.error("Error deleting testimonial:", error);
      toast.error("Failed to delete testimonial");
    } finally {
      setTestimonialActionLoading(false);
    }
  };

  const handleLaunchSplashFileChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLaunchSplashFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLaunchSplashPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return previewUrl;
    });
  };

  const handleCreateLaunchSplash = async () => {
    if (!launchSplashFile) {
      toast.error("Please select an image to upload.");
      return;
    }

    setLaunchSplashUploadLoading(true);
    try {
      await createLaunchSplash({
        file: launchSplashFile,
        altText: launchSplashAltText.trim() || undefined,
        routePath: launchSplashRoute.trim() || undefined,
        createdBy: currentUser?.email || currentUser?.uid || null,
      });
      toast.success("Launch Splash Modal image uploaded");
      setLaunchSplashFile(null);
      if (launchSplashPreview) {
        URL.revokeObjectURL(launchSplashPreview);
      }
      setLaunchSplashPreview(null);
      setLaunchSplashAltText("");
      setLaunchSplashRoute("");
      if (launchSplashInputRef.current) {
        launchSplashInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error creating launch splash image:", error);
      const message =
        error instanceof Error ? error.message : "Failed to upload splash image";
      toast.error(message);
    } finally {
      setLaunchSplashUploadLoading(false);
    }
  };

  const handleLaunchSplashStatusChange = async (
    image: LaunchSplashImage,
    status: LaunchSplashStatus
  ) => {
    setLaunchSplashActionId(image.id);
    try {
      await updateLaunchSplashStatus(image.id, status);
      toast.success(
        status === "active"
          ? "Launch splash activated"
          : "Launch splash set to inactive"
      );
    } catch (error) {
      console.error("Error updating launch splash status:", error);
      toast.error("Failed to update launch splash status");
    } finally {
      setLaunchSplashActionId(null);
    }
  };

  const handleConfirmLaunchSplashDelete = async () => {
    if (!pendingLaunchSplashDelete) return;

    setLaunchSplashActionId(pendingLaunchSplashDelete.id);
    try {
      await deleteLaunchSplash(pendingLaunchSplashDelete);
      toast.success("Launch splash image deleted");
      setPendingLaunchSplashDelete(null);
    } catch (error) {
      console.error("Error deleting launch splash image:", error);
      toast.error("Failed to delete launch splash image");
    } finally {
      setLaunchSplashActionId(null);
    }
  };

  // Handle view on site
  const handleViewOnSite = () => {
    // Open the customer-facing website in a new tab
    const customerSiteUrl =
      import.meta.env.VITE_APP_NODE_ENV === "production"
        ? "https://jampackd.store"
        : "http://localhost:3000";

    window.open(customerSiteUrl, "_blank", "noopener,noreferrer");
  };

  // Show error if content loading fails
  useEffect(() => {
    if (bannersError || faqsError || testimonialsError || launchSplashError) {
      toast.error(
        bannersError || faqsError || testimonialsError || launchSplashError
      );
    }
  }, [bannersError, faqsError, testimonialsError, launchSplashError]);

  useEffect(() => {
    return () => {
      if (launchSplashPreview) {
        URL.revokeObjectURL(launchSplashPreview);
      }
    };
  }, [launchSplashPreview]);

  const renderBannersTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layout className="h-5 w-5 text-jamaica-green" />
            Website Banners ({banners.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {bannersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading banners...</span>
            </div>
          ) : bannersError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Banners
              </h3>
              <p className="text-gray-600 mb-4">{bannersError}</p>
              <Button onClick={handleRefresh} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : banners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Layout className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Banners Found
              </h3>
              <p className="text-gray-600 mb-4">
                No website banners have been configured yet.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={() => setIsCreateBannerModalOpen(true)}
              >
                <Plus className="w-4 h-4" />
                Create First Banner
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Banner</TableHead>
                    <TableHead>Theme</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Features</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {banners.map((banner, index) => {
                    const statusConfig = getStatusConfig(
                      banner.isActive ? "active" : "inactive"
                    );
                    const themeConfig = getBannerBackgroundConfig(
                      banner.backgroundColor
                    );

                    return (
                      <motion.tr
                        key={banner.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-12 h-8 rounded-lg flex items-center justify-center ${
                                banner.backgroundColor === "jamaica-green"
                                  ? "bg-jamaica-green"
                                  : "bg-jamaica-black"
                              }`}
                            >
                              <Globe className="w-4 h-4 text-white" />
                            </div>
                            <div className="space-y-1 flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate" title={banner.text || "Untitled Banner"}>
                                {banner.text ? 
                                  (banner.text.length > 60 ? 
                                    `${banner.text.substring(0, 60)}...` : 
                                    banner.text
                                  ) : 
                                  "Untitled Banner"
                                }
                              </p>
                              <p className="text-xs text-gray-400 flex items-center gap-1">
                                <span>Banner {banner.order}</span>
                                {banner.isActive && (
                                  <>
                                    <span>•</span>
                                    <span className="text-green-600">
                                      Live on website
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={themeConfig.color}>
                            {themeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className={statusConfig.color}>
                              {statusConfig.label}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleBannerStatus(banner)}
                              className="h-6 w-6 p-0"
                            >
                              {banner.isActive ? (
                                <Power className="w-3 h-3 text-green-600" />
                              ) : (
                                <PowerOff className="w-3 h-3 text-gray-400" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            {banner.showCloseButton && (
                              <Badge variant="outline" className="text-xs">
                                <MousePointer className="w-3 h-3 mr-1" />
                                Dismissible
                              </Badge>
                            )}
                            {banner.countdown?.enabled && (
                              <Badge variant="outline" className="text-xs">
                                <Timer className="w-3 h-3 mr-1" />
                                Countdown
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-600">
                            {banner.updatedAt
                              ? formatDate(banner.updatedAt)
                              : "N/A"}
                          </p>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleEditBanner(banner)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Banner
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={handleViewOnSite}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                View on Site
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleToggleBannerStatus(banner)}
                                className={
                                  banner.isActive
                                    ? "text-red-600"
                                    : "text-green-600"
                                }
                              >
                                {banner.isActive ? (
                                  <>
                                    <PowerOff className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <Power className="w-4 h-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderLaunchSplashTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-jamaica-green" />
            Launch Splash Modal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-4">
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Upload splash image
                    </p>
                    <p className="text-xs text-gray-500">
                      JPG, PNG, WEBP, or GIF up to 5MB. Shown in full without
                      distortion on first load.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => launchSplashInputRef.current?.click()}
                    disabled={launchSplashUploadLoading}
                  >
                    <Upload className="w-4 h-4" />
                    Choose image
                  </Button>
                  <input
                    ref={launchSplashInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleLaunchSplashFileChange}
                  />
                </div>

                {launchSplashPreview && (
                  <div className="mt-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="relative w-full overflow-hidden rounded-lg bg-gray-100">
                      <img
                        src={launchSplashPreview}
                        alt="Launch splash preview"
                        className="w-full max-h-[360px] object-contain"
                      />
                    </div>
                    <p className="mt-3 text-xs text-gray-500">
                      Customers will see the full image with subtle rounding and
                      an overlay close control.
                    </p>
                  </div>
                )}

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-700">
                      Alt text (recommended)
                    </p>
                    <Input
                      placeholder="Describe the visual for accessibility"
                      value={launchSplashAltText}
                      onChange={(e) => setLaunchSplashAltText(e.target.value)}
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-gray-700">
                      Optional route
                    </p>
                    <Input
                      placeholder="e.g. /products or /bundles/summer"
                      value={launchSplashRoute}
                      onChange={(e) => setLaunchSplashRoute(e.target.value)}
                    />
                  </div>
                  <div className="rounded-lg border border-gray-100 bg-white p-3 text-xs text-gray-600 shadow-sm">
                    <div className="font-semibold text-gray-900">
                      Launch Splash rules
                    </div>
                    <ul className="mt-2 list-inside list-disc space-y-1">
                      <li>Only one image can be active at a time.</li>
                      <li>Uploads start inactive so you can review.</li>
                      <li>Close button appears for customers on first load.</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                  {launchSplashFile && (
                    <Badge
                      variant="secondary"
                      className="bg-gray-100 text-gray-800"
                    >
                      {launchSplashFile.name}
                    </Badge>
                  )}
                  <Button
                    onClick={handleCreateLaunchSplash}
                    disabled={!launchSplashFile || launchSplashUploadLoading}
                    className="bg-jamaica-green text-white hover:bg-jamaica-green/90"
                  >
                    {launchSplashUploadLoading ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload & Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="admin-card">
        <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-jamaica-green" />
            Launch Splash Library ({launchSplashImages.length})
          </CardTitle>
          <Tabs
            value={launchSplashFilter}
            onValueChange={(value) =>
              setLaunchSplashFilter(value as LaunchSplashStatus | "all")
            }
          >
            <TabsList>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="inactive">Inactive</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="p-0">
          {launchSplashLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">
                Loading Launch Splash Modals...
              </span>
            </div>
          ) : launchSplashError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="mb-4 h-12 w-12 text-red-500" />
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                Error Loading Launch Splash Modals
              </h3>
              <p className="mb-4 text-gray-600">{launchSplashError}</p>
              <Button onClick={handleRefresh} variant="outline">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          ) : filteredLaunchSplashImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ImageIcon className="mb-4 h-12 w-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {launchSplashFilter === "active"
                  ? "No active Launch Splash Modals"
                  : "No Launch Splash Modals yet"}
              </h3>
              <p className="mb-4 text-gray-600">
                Upload an image above and activate it to greet customers on
                first load.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={() => launchSplashInputRef.current?.click()}
              >
                <Upload className="h-4 w-4" />
                Upload Splash Image
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Preview</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLaunchSplashImages.map((image, index) => {
                    const statusConfig = getStatusConfig(image.status);
                    return (
                      <motion.tr
                        key={image.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="transition-colors hover:bg-gray-50"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-16 w-20 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                              <img
                                src={image.url}
                                alt={image.altText || "Launch splash preview"}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div className="space-y-1">
                              <p className="font-medium text-gray-900">
                                {image.fileName || "Launch Splash Modal"}
                              </p>
                              <p className="text-xs text-gray-500">
                                {image.width && image.height
                                  ? `${image.width} × ${image.height}`
                                  : "Auto-sized"}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-900">
                          {image.altText || "No alt text"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {image.createdBy || "Admin portal"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {image.routePath ? (
                        <Badge variant="outline" className="text-xs">
                          {image.routePath}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-gray-600">
                        {image.createdAt
                          ? formatDate(image.createdAt)
                          : "Just now"}
                          </p>
                          {image.updatedAt && (
                            <p className="text-xs text-gray-400">
                              Updated {formatDate(image.updatedAt)}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleLaunchSplashStatusChange(
                                    image,
                                    image.status === "active"
                                      ? "inactive"
                                      : "active"
                                  )
                                }
                                disabled={launchSplashActionId === image.id}
                              >
                                {launchSplashActionId === image.id ? (
                                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                                ) : image.status === "active" ? (
                                  <PowerOff className="mr-2 h-4 w-4" />
                                ) : (
                                  <Power className="mr-2 h-4 w-4" />
                                )}
                                {image.status === "active"
                                  ? "Set Inactive"
                                  : "Set Active"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => window.open(image.url, "_blank")}
                              >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Open full image
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() =>
                                  setPendingLaunchSplashDelete(image)
                                }
                                disabled={launchSplashActionId === image.id}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderFAQsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-jamaica-green" />
            Frequently Asked Questions ({faqs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {faqsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">Loading FAQs...</span>
            </div>
          ) : faqsError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading FAQs
              </h3>
              <p className="text-gray-600 mb-4">{faqsError}</p>
              <Button onClick={handleRefresh} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : faqs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No FAQs Found
              </h3>
              <p className="text-gray-600 mb-4">
                No frequently asked questions have been created yet.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={() => setIsCreateFAQModalOpen(true)}
              >
                <Plus className="w-4 h-4" />
                Create First FAQ
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Question</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Performance</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faqs.map((faq, index) => {
                    const statusConfig = getStatusConfig(
                      faq.isPublished ? "published" : "draft"
                    );

                    return (
                      <motion.tr
                        key={faq.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900 line-clamp-2">
                              {faq.question}
                            </p>
                            <p className="text-sm text-gray-500 line-clamp-2">
                              {faq.answer}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{faq.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900">
                              {(faq.views || 0).toLocaleString()} views
                            </p>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                <span className="text-xs text-green-600">
                                  {faq.isHelpful || 0}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <AlertCircle className="w-3 h-3 text-red-500" />
                                <span className="text-xs text-red-600">
                                  {faq.isNotHelpful || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-600">
                            {formatDate(faq.updatedAt!)}
                          </p>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleEditFAQ(faq)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit FAQ
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleFAQDeleteAction(faq)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete FAQ
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderTestimonialsTab = () => (
    <div className="space-y-6">
      <Card className="admin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-jamaica-green" />
            Customer Testimonials ({testimonials.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {testimonialsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-jamaica-green" />
              <span className="ml-2 text-gray-600">
                Loading testimonials...
              </span>
            </div>
          ) : testimonialsError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Testimonials
              </h3>
              <p className="text-gray-600 mb-4">{testimonialsError}</p>
              <Button onClick={handleRefresh} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Star className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Testimonials Found
              </h3>
              <p className="text-gray-600 mb-4">
                No customer testimonials have been submitted yet.
              </p>
              <Button
                className="flex items-center gap-2"
                onClick={() => setIsCreateTestimonialModalOpen(true)}
              >
                <Plus className="w-4 h-4" />
                Add Testimonial
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Product ID</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testimonials.map((testimonial, index) => {
                    const statusConfig = getStatusConfig(testimonial.status);

                    return (
                      <motion.tr
                        key={testimonial.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900">
                              {testimonial.customerName}
                            </p>
                            {testimonial.customerLocation && (
                              <p className="text-sm text-gray-500">
                                {testimonial.customerLocation}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-900">
                            {testimonial.productId || "General"}
                          </p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < testimonial.rating
                                    ? "text-jamaica-yellow fill-jamaica-yellow"
                                    : "text-gray-300"
                                }`}
                              />
                            ))}
                            <span className="ml-1 text-sm text-gray-600">
                              ({testimonial.rating})
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-900 line-clamp-2 max-w-xs">
                            {testimonial.content}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-600">
                            {formatDate(testimonial.createdAt!)}
                          </p>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  handleEditTestimonial(testimonial)
                                }
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  const action =
                                    testimonial.status === "published"
                                      ? "unpublish"
                                      : "publish";
                                  handleTestimonialStatusAction(
                                    testimonial,
                                    action
                                  );
                                }}
                              >
                                {testimonial.status === "published" ? (
                                  <>
                                    <EyeOff className="w-4 h-4 mr-2" />
                                    Unpublish
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Publish
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() =>
                                  handleTestimonialDeleteAction(testimonial)
                                }
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes fadeInSlide {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
      <div className="space-y-6">
        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex justify-between items-center"
        >
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={
                bannersLoading ||
                faqsLoading ||
                testimonialsLoading ||
                launchSplashLoading
              }
              className="flex items-center gap-2"
            >
              {bannersLoading ||
              faqsLoading ||
              testimonialsLoading ||
              launchSplashLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh
            </Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-jamaica-green hover:bg-jamaica-green/90 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Create Content
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setIsCreateBannerModalOpen(true)}
              >
                <Layout className="w-4 h-4 mr-2" />
                Create Banner
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedTab("launchSplash");
                  setTimeout(() => {
                    launchSplashInputRef.current?.click();
                  }, 150);
                }}
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Upload Launch Splash
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsCreateFAQModalOpen(true)}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Create FAQ
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setIsCreateTestimonialModalOpen(true)}
              >
                <Star className="w-4 h-4 mr-2" />
                Create Testimonial
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Active Banners
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {activeBannerCount}
                  </p>
                </div>
                <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                  <Layout className="h-6 w-6 text-jamaica-green" />
                </div>
              </div>
              <div className="h-5 mt-4"></div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Published FAQs
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {publishedFAQsCount}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <span className="text-gray-600">
                  {faqsPublishedPercentage}% published
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Published Reviews
                  </p>
                  <p className="text-2xl font-bold text-jamaica-yellow">
                    {publishedTestimonialsCount}
                  </p>
                </div>
                <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                  <Star className="h-6 w-6 text-jamaica-yellow" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <span className="text-gray-600">
                  {testimonialAverageRating} avg rating
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Content
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalActiveContentCount}
                  </p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="flex items-center mt-4 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-green-600">All up to date</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Content Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white rounded-xl shadow-md border border-gray-100"
        >
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              {[
                { id: "banners", label: "Banners", icon: Layout },
                {
                  id: "launchSplash",
                  label: "Launch Splash Modal",
                  icon: ImageIcon,
                },
                { id: "faqs", label: "FAQs", icon: MessageSquare },
                { id: "testimonials", label: "Testimonials", icon: Star },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setSelectedTab(id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    selectedTab === id
                      ? "border-jamaica-green text-jamaica-green"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {selectedTab === "banners" && renderBannersTab()}
            {selectedTab === "launchSplash" && renderLaunchSplashTab()}
            {selectedTab === "faqs" && renderFAQsTab()}
            {selectedTab === "testimonials" && renderTestimonialsTab()}
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      {isCreateBannerModalOpen && (
        <CreateBannerModal
          isOpen={isCreateBannerModalOpen}
          onClose={() => setIsCreateBannerModalOpen(false)}
          onSubmit={addBanner}
        />
      )}
      {isCreateFAQModalOpen && (
        <CreateFAQModal
          isOpen={isCreateFAQModalOpen}
          onClose={() => setIsCreateFAQModalOpen(false)}
          onSubmit={addFAQ}
          existingFAQs={faqs}
        />
      )}
      {isCreateTestimonialModalOpen && (
        <CreateTestimonialModal
          isOpen={isCreateTestimonialModalOpen}
          onClose={() => setIsCreateTestimonialModalOpen(false)}
          onSubmit={addTestimonial}
        />
      )}
      {isEditBannerModalOpen && (
        <EditBannerModal
          isOpen={isEditBannerModalOpen}
          onClose={() => {
            setIsEditBannerModalOpen(false);
            setSelectedBanner(null);
          }}
          banner={selectedBanner}
          onSubmit={updateBanner}
        />
      )}
      {isEditFAQModalOpen && (
        <EditFAQModal
          isOpen={isEditFAQModalOpen}
          onClose={() => {
            setIsEditFAQModalOpen(false);
            setSelectedFAQ(null);
          }}
          faq={selectedFAQ}
          onSubmit={async (faqId: string, faqData: Partial<AdminFAQ>) => {
            try {
              await updateFAQ(faqId, faqData);
              setIsEditFAQModalOpen(false);
              setSelectedFAQ(null);
            } catch (error) {
              console.error("Error updating FAQ:", error);
            }
          }}
          existingFAQs={faqs}
        />
      )}
      {isEditTestimonialModalOpen && (
        <EditTestimonialModal
          isOpen={isEditTestimonialModalOpen}
          onClose={() => {
            setIsEditTestimonialModalOpen(false);
            setSelectedTestimonial(null);
          }}
          testimonial={selectedTestimonial}
          onSubmit={async (
            testimonialId: string,
            testimonialData: Partial<AdminTestimonial>
          ) => {
            try {
              await updateTestimonial(testimonialId, testimonialData);
              setIsEditTestimonialModalOpen(false);
              setSelectedTestimonial(null);
            } catch (error) {
              console.error("Error updating testimonial:", error);
            }
          }}
        />
      )}

      {/* Launch Splash Delete Confirmation Modal */}
      <DeleteConfirmDialog
        isOpen={!!pendingLaunchSplashDelete}
        onClose={() => setPendingLaunchSplashDelete(null)}
        onConfirm={handleConfirmLaunchSplashDelete}
        loading={
          !!pendingLaunchSplashDelete &&
          launchSplashActionId === pendingLaunchSplashDelete.id
        }
        title="Delete Launch Splash"
        description="Are you sure you want to delete this Launch Splash Modal image? This action cannot be undone."
        itemName={
          pendingLaunchSplashDelete?.fileName ||
          pendingLaunchSplashDelete?.altText ||
          "Launch Splash Modal"
        }
      />

      {/* FAQ Delete Confirmation Modal */}
      <DeleteConfirmDialog
        isOpen={showDeleteFAQModal}
        onClose={() => {
          setShowDeleteFAQModal(false);
          setPendingFAQAction(null);
        }}
        onConfirm={handleConfirmFAQDelete}
        loading={faqActionLoading}
        title="Delete FAQ"
        description="Are you sure you want to delete this FAQ? This action cannot be undone."
        itemName={pendingFAQAction?.faq?.question}
      />

      {/* Testimonial Status Change Confirmation Modal */}
      <ConfirmDialog
        isOpen={showUnpublishTestimonialModal}
        onClose={() => {
          setShowUnpublishTestimonialModal(false);
          setPendingTestimonialAction(null);
        }}
        onConfirm={handleConfirmTestimonialStatusChange}
        loading={testimonialActionLoading}
        title={
          pendingTestimonialAction?.action === "publish"
            ? "Publish Testimonial"
            : "Unpublish Testimonial"
        }
        description={
          pendingTestimonialAction?.action === "publish"
            ? "Are you sure you want to publish this testimonial? It will be visible to customers on the website."
            : "Are you sure you want to unpublish this testimonial? It will be hidden from customers on the website."
        }
        itemName={pendingTestimonialAction?.testimonial?.customerName}
        confirmText={
          pendingTestimonialAction?.action === "publish"
            ? "Publish"
            : "Unpublish"
        }
        variant={
          pendingTestimonialAction?.action === "publish"
            ? "default"
            : "destructive"
        }
        type={
          pendingTestimonialAction?.action === "publish" ? "enable" : "disable"
        }
      />

      {/* Testimonial Delete Confirmation Modal */}
      <DeleteConfirmDialog
        isOpen={showDeleteTestimonialModal}
        onClose={() => {
          setShowDeleteTestimonialModal(false);
          setPendingTestimonialAction(null);
        }}
        onConfirm={handleConfirmTestimonialDelete}
        loading={testimonialActionLoading}
        title="Delete Testimonial"
        description="Are you sure you want to delete this testimonial? This action cannot be undone."
        itemName={pendingTestimonialAction?.testimonial?.customerName}
      />
    </>
  );
}
